import { useEffect, useState } from 'hono/jsx';
import { Layout } from '../components/Layout';
import { PageSpinner } from '../components/Spinner';
import { api } from '../lib/api';
import { showToast } from '../lib/toast';
import { useRoute, parseQuery } from '../lib/router';

interface AffiliateData {
  earnings: { totalEarned: number; pendingBalance: number; totalPaid: number };
  stats: { totalReferrals: number };
  stripe: { connected: boolean; onboardingComplete: boolean };
}

interface Commission {
  id: string;
  createdAt: string;
  site?: { name: string };
  type: 'setup' | 'recurring' | string;
  paymentAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: 'completed' | 'failed' | string;
}

export function AffiliateDashboard() {
  const path = useRoute();
  const params = parseQuery(path);
  const [data, setData] = useState<AffiliateData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [notAffiliate, setNotAffiliate] = useState<boolean>(false);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [onboarding, setOnboarding] = useState<boolean>(false);

  useEffect(() => {
    if (params.get('onboard') === 'complete') {
      showToast('success', 'Onboarding Complete', 'Your Stripe account is now connected!');
      window.history.replaceState({}, '', '/affiliate');
    } else if (params.get('onboard') === 'refresh') {
      showToast('info', 'Onboarding Expired', 'Starting a new onboarding session...');
      window.history.replaceState({}, '', '/affiliate');
    }
    (async () => {
      try {
        const r = await api('/affiliates/me');
        if (r.status === 404) { setNotAffiliate(true); return; }
        if (!r.ok) throw new Error('Failed to load affiliate data');
        const d = await r.json();
        setData(d);
        if (params.get('onboard') === 'refresh') startOnboarding();
        loadCommissions();
      } catch (e: any) {
        setNotAffiliate(true);
      } finally { setLoading(false); }
    })();
  }, []);

  async function loadCommissions() {
    try {
      const r = await api('/affiliates/commissions?limit=20');
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setCommissions(d.commissions || []);
    } catch {}
  }

  async function startOnboarding() {
    setOnboarding(true);
    try {
      const r = await api('/affiliates/onboard', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).error || 'Failed to start onboarding'); }
      const d = await r.json();
      window.location.href = (d as any).onboardingUrl;
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to start onboarding');
      setOnboarding(false);
    }
  }

  async function openStripeDashboard() {
    try {
      const r = await api('/affiliates/dashboard-link');
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).error || 'Failed to get dashboard link'); }
      const d = await r.json();
      window.open((d as any).url, '_blank');
    } catch (err: any) {
      showToast('error', 'Error', err?.message || 'Failed to open Stripe dashboard');
    }
  }

  return (
    <Layout active="affiliate">
      <div class="mb-8">
        <h1 class="text-2xl font-bold text-slate-100">Affiliate Dashboard</h1>
        <p class="text-slate-400 mt-1">Track your referrals, commissions, and payouts</p>
      </div>

      {loading ? (
        <PageSpinner label="Loading affiliate data…" />
      ) : notAffiliate ? (
        <div class="bg-dark-800 rounded-xl border border-slate-700/50 p-8 text-center max-w-md mx-auto">
          <div class="w-12 h-12 rounded-full bg-dark-700 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
          </div>
          <h2 class="text-lg font-semibold text-slate-100 mb-2">Not an Affiliate</h2>
          <p class="text-slate-400 text-sm">You don't have an affiliate account yet. Contact us to become an affiliate partner.</p>
        </div>
      ) : data ? (
        <>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Stat label="Total Earned" value={`$${data.earnings.totalEarned.toFixed(2)}`} color="text-emerald-400" />
            <Stat label="Pending Balance" value={`$${data.earnings.pendingBalance.toFixed(2)}`} color="text-amber-400" />
            <Stat label="Total Paid Out" value={`$${data.earnings.totalPaid.toFixed(2)}`} color="text-cyan-400" />
            <Stat label="Referrals" value={String(data.stats.totalReferrals)} color="text-slate-100" />
          </div>

          <div class="mb-8 bg-dark-800 rounded-xl border border-slate-700/50 p-6">
            <h3 class="text-lg font-semibold text-slate-100 mb-4">Stripe Account</h3>
            {!data.stripe.connected ? (
              <StripeCard
                color="amber" iconPath="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                title="Connect Your Stripe Account"
                desc="To receive commission payouts, you need to complete Stripe onboarding. This only takes a few minutes."
                actionLabel={onboarding ? 'Redirecting…' : 'Connect Stripe Account'}
                onAction={startOnboarding} disabled={onboarding}
              />
            ) : !data.stripe.onboardingComplete ? (
              <StripeCard
                color="amber" iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                title="Onboarding Incomplete"
                desc="Please complete your Stripe account setup to start receiving payouts."
                actionLabel={onboarding ? 'Redirecting…' : 'Continue Setup'}
                onAction={startOnboarding} disabled={onboarding}
              />
            ) : (
              <StripeCard
                color="emerald" iconPath="M5 13l4 4L19 7"
                title="Account Connected"
                desc="Your Stripe account is set up and ready to receive payouts."
                actionLabel="View Stripe Dashboard" onAction={openStripeDashboard}
                neutral
              />
            )}
          </div>

          <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-hidden">
            <div class="px-6 py-4 border-b border-slate-700/50"><h3 class="text-lg font-semibold text-slate-100">Commission History</h3></div>
            {commissions.length === 0 ? (
              <div class="p-8 text-center text-slate-400">No commissions yet. Share your referral link to start earning!</div>
            ) : (
              <div class="overflow-x-auto"><table class="w-full">
                <thead class="bg-dark-700"><tr>
                  <Th>Date</Th><Th>Site</Th><Th>Type</Th><Th>Payment</Th><Th>Commission</Th><Th>Status</Th>
                </tr></thead>
                <tbody class="divide-y divide-slate-700/50">
                  {commissions.map((c) => (
                    <tr>
                      <td class="px-4 py-3 text-sm text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</td>
                      <td class="px-4 py-3 text-sm text-slate-200">{c.site?.name || '-'}</td>
                      <td class="px-4 py-3"><span class={`text-xs px-2 py-1 rounded ${c.type === 'setup' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'}`}>{c.type}</span></td>
                      <td class="px-4 py-3 text-sm text-slate-300">${c.paymentAmount.toFixed(2)}</td>
                      <td class="px-4 py-3 text-sm text-emerald-400">${c.commissionAmount.toFixed(2)} ({c.commissionRate}%)</td>
                      <td class="px-4 py-3"><span class={`text-xs px-2 py-1 rounded ${c.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : c.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      ) : null}
    </Layout>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div class="bg-dark-800 rounded-xl border border-slate-700/50 p-5">
      <p class="text-sm text-slate-400 mb-1">{label}</p>
      <p class={`text-2xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function Th({ children }: { children: any }) {
  return <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">{children}</th>;
}

function StripeCard({ color, iconPath, title, desc, actionLabel, onAction, disabled = false, neutral = false }: {
  color: 'amber' | 'emerald';
  iconPath: string;
  title: string;
  desc: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
  neutral?: boolean;
}) {
  const bg = color === 'amber' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400';
  const btn = neutral
    ? 'bg-dark-700 text-slate-200 hover:bg-dark-600'
    : 'bg-cyan-500 text-dark-900 hover:bg-cyan-400';
  return (
    <div class="flex items-start gap-4">
      <div class={`w-10 h-10 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={iconPath}/></svg>
      </div>
      <div class="flex-1">
        <p class="text-slate-200 font-medium">{title}</p>
        <p class="text-sm text-slate-400 mt-1 mb-4">{desc}</p>
        <button type="button" onClick={onAction} disabled={disabled} class={`${btn} px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60`}>{actionLabel}</button>
      </div>
    </div>
  );
}
